import type { AccentName } from "./accent.js";
import { COLOR, css, darken, lighten } from "./paint.js";

/**
 * The creatures, drawn as inline SVG.
 *
 * One per activity family, so a child recognises a game before they can read its name — the
 * research on this age band is consistent that they navigate by picture and colour, not text.
 *
 * Drawn from circles and paths rather than loaded as art, for three reasons that all matter
 * here: there is no asset to download on a metered connection, nothing to redraw when a game
 * changes, and the colour comes from the family token so the creature and its card can never
 * disagree. **Eyes are the whole trick.** A blob with two eyes and a highlight reads as a
 * character; the same blob without them reads as a loading spinner.
 *
 * Each has a `blink` and a `bob` animation driven by CSS, staggered by `step` so a row of them is
 * not synchronised — synchronised idle motion looks mechanical, offset motion looks alive. The
 * keyframes and the eight delay steps are in `app.css`; this component only names them.
 */

export type CreatureKind =
  | "firefly"
  | "owl"
  | "fish"
  | "cat"
  | "star"
  | "mouse"
  | "robot"
  | "turtle"
  | "bird"
  | "chameleon"
  | "elephant"
  | "penguin"
  | "bee"
  | "frog"
  | "ladybird"
  | "parrot"
  | "squirrel"
  | "bat"
  | "butterfly"
  | "beaver"
  | "ant"
  | "octopus"
  | "snail"
  | "hare";

interface CreatureProps {
  kind: CreatureKind;
  /** Which accent this creature wears. Resolved to fills below. */
  accent: AccentName;
  size?: number;
  /**
   * Which stagger step this creature sits on, so siblings do not move in lockstep.
   *
   * A step rather than a number of seconds: a computed delay can only be an inline style, and
   * eight offsets are indistinguishable from a continuous scatter. Wraps, so any index works.
   */
  step?: number;
  /** Suppresses the idle motion. Used inside a game, where the creature must not distract. */
  still?: boolean;
}

/** Static class strings, so Tailwind emits them. `step` wraps, so any index is valid. */
const DELAY = [
  "kd-delay-0",
  "kd-delay-1",
  "kd-delay-2",
  "kd-delay-3",
  "kd-delay-4",
  "kd-delay-5",
  "kd-delay-6",
  "kd-delay-7",
] as const;

/**
 * A pair of eyes, positioned in a 100×100 viewBox.
 *
 * Shared because "how do the eyes look" should be one answer, and because the highlight dot is
 * the detail that turns a black oval into something looking back at you.
 */
function Eyes({
  cx = 50,
  cy = 46,
  spread = 13,
  r = 7.5,
  motion = "",
  still = false,
}: {
  cx?: number;
  cy?: number;
  spread?: number;
  r?: number;
  /** The delay utility for this instance, so a row of creatures does not blink in unison. */
  motion?: string;
  still?: boolean;
}) {
  return (
    <g className={still ? undefined : `kd-blink ${motion}`}>
      {/* Keyed by index, not by offset: a single-eyed creature passes spread 0, which made both
          keys `0` and had React reconciling two children as one. */}
      {[-spread, spread].map((dx, i) => (
        <g key={i}>
          <circle cx={cx + dx} cy={cy} r={r} fill={css(COLOR.ink)} />
          {/* Off-centre highlight: the difference between an eye and a dot. */}
          <circle cx={cx + dx + r * 0.32} cy={cy - r * 0.34} r={r * 0.3} fill="#fff" />
        </g>
      ))}
    </g>
  );
}

/** A soft smile. An arc, never a filled shape — a filled mouth reads as a grimace at this size. */
function Smile({ cy = 64, w = 15 }: { cy?: number; w?: number }) {
  return (
    <path
      d={`M ${50 - w} ${cy} Q 50 ${cy + w * 0.75} ${50 + w} ${cy}`}
      stroke={css(COLOR.ink)}
      strokeWidth={4}
      strokeLinecap="round"
      fill="none"
      opacity={0.85}
    />
  );
}

export function Creature({ kind, accent, size = 72, step = 0, still = false }: CreatureProps) {
  const color = COLOR[accent];

  const light = css(lighten(color, 0.34));
  const base = css(color);
  const deep = css(darken(color, 0.18));
  const gradientId = `kd-grad-${kind}-${color.toString(16)}`;

  const motion = DELAY[step % DELAY.length] ?? DELAY[0];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="presentation"
      className="block overflow-visible"
    >
      <defs>
        {/* A two-stop gradient on every body. Flat fills are what made the old tiles look
            like placeholders. */}
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={light} />
          <stop offset="1" stopColor={base} />
        </linearGradient>
      </defs>

      <g className={still ? undefined : `kd-bob ${motion}`}>
        {kind === "firefly" && (
          <>
            {/* The glow is the point of a firefly, and it is also the span game's whole
                metaphor: something lights up, then you light it up again. */}
            <circle
              cx={50}
              cy={52}
              r={40}
              fill={base}
              className={still ? "opacity-30" : `kd-glow ${motion}`}
            />
            <ellipse cx={50} cy={54} rx={30} ry={28} fill={`url(#${gradientId})`} />
            {/* Wings behind the body, translucent so the glow reads through them. */}
            <ellipse cx={22} cy={38} rx={16} ry={10} fill="#fff" opacity={0.5} transform="rotate(-24 22 38)" />
            <ellipse cx={78} cy={38} rx={16} ry={10} fill="#fff" opacity={0.5} transform="rotate(24 78 38)" />
            <Eyes cy={50} spread={11} r={7} motion={motion} still={still} />
            <Smile cy={66} w={12} />
          </>
        )}

        {kind === "owl" && (
          <>
            {/* Backward span: an owl, because an owl turns its head all the way round. */}
            <ellipse cx={50} cy={56} rx={32} ry={34} fill={`url(#${gradientId})`} />
            {/* Ear tufts. */}
            <path d={`M 24 30 L 30 12 L 42 26 Z`} fill={deep} />
            <path d={`M 76 30 L 70 12 L 58 26 Z`} fill={deep} />
            {/* Face disc, which is what makes an owl an owl. */}
            <ellipse cx={50} cy={48} rx={26} ry={22} fill="#fff" opacity={0.92} />
            <Eyes cy={47} spread={12} r={8.5} motion={motion} still={still} />
            {/* Beak. */}
            <path d={`M 50 56 L 45 64 L 55 64 Z`} fill={css(COLOR.sunny)} />
            <ellipse cx={50} cy={82} rx={20} ry={8} fill={deep} opacity={0.5} />
          </>
        )}

        {kind === "fish" && (
          <>
            {/* Pattern matching: a fish, because scales are patterns. */}
            <path d={`M 84 50 L 100 34 L 100 66 Z`} fill={deep} />
            <ellipse cx={46} cy={50} rx={36} ry={27} fill={`url(#${gradientId})`} />
            {/* Two scale arcs. Enough to say "patterned" without becoming noise. */}
            <path d="M 44 26 Q 58 50 44 74" stroke="#fff" strokeWidth={3.5} fill="none" opacity={0.45} />
            <path d="M 60 30 Q 72 50 60 70" stroke="#fff" strokeWidth={3.5} fill="none" opacity={0.32} />
            <Eyes cx={30} cy={44} spread={0} r={8} motion={motion} still={still} />
            <path
              d="M 20 58 Q 27 64 34 58"
              stroke={css(COLOR.ink)}
              strokeWidth={3.5}
              strokeLinecap="round"
              fill="none"
              opacity={0.8}
            />
          </>
        )}

        {kind === "cat" && (
          <>
            {/* Odd one out: a cat, because a cat is the one spotting what is different. */}
            <path d={`M 22 34 L 26 12 L 44 26 Z`} fill={`url(#${gradientId})`} />
            <path d={`M 78 34 L 74 12 L 56 26 Z`} fill={`url(#${gradientId})`} />
            <path d="M 27 22 L 29 15 L 38 25 Z" fill={css(COLOR.watermelon)} opacity={0.55} />
            <path d="M 73 22 L 71 15 L 62 25 Z" fill={css(COLOR.watermelon)} opacity={0.55} />
            <ellipse cx={50} cy={56} rx={33} ry={30} fill={`url(#${gradientId})`} />
            <Eyes cy={52} spread={13} r={8} motion={motion} still={still} />
            <path d={`M 50 62 L 45 68 L 55 68 Z`} fill={css(COLOR.watermelon)} />
            {/* Whiskers. Thin, low opacity — at 72px they read as texture, not lines. */}
            {[[-1, 0], [-1, 6], [1, 0], [1, 6]].map(([dir, dy], i) => (
              <line
                key={i}
                x1={50 + (dir ?? 1) * 14}
                y1={68 + (dy ?? 0) * 0.5}
                x2={50 + (dir ?? 1) * 34}
                y2={66 + (dy ?? 0)}
                stroke={css(COLOR.ink)}
                strokeWidth={2.5}
                strokeLinecap="round"
                opacity={0.35}
              />
            ))}
          </>
        )}

        {kind === "mouse" && (
          <>
            {/* The maze: a mouse, because a mouse is what finds its way through one. */}
            <circle cx={26} cy={30} r={13} fill={deep} />
            <circle cx={74} cy={30} r={13} fill={deep} />
            <circle cx={26} cy={30} r={7} fill={css(COLOR.watermelon)} opacity={0.5} />
            <circle cx={74} cy={30} r={7} fill={css(COLOR.watermelon)} opacity={0.5} />
            <ellipse cx={50} cy={56} rx={31} ry={29} fill={`url(#${gradientId})`} />
            <Eyes cy={52} spread={12} r={7.5} motion={motion} still={still} />
            <circle cx={50} cy={66} r={4.5} fill={css(COLOR.watermelon)} />
            {/* A tail, curling away. The one line in these that is pure character. */}
            <path
              d="M 80 72 Q 96 74 92 58"
              stroke={deep}
              strokeWidth={4}
              strokeLinecap="round"
              fill="none"
            />
          </>
        )}

        {kind === "robot" && (
          <>
            {/* Robot engineer. Square where the others are round, so it reads as built. */}
            <line x1={50} y1={22} x2={50} y2={8} stroke={deep} strokeWidth={4} strokeLinecap="round" />
            <circle cx={50} cy={8} r={6} fill={css(COLOR.sunny)} />
            <rect x={22} y={24} width={56} height={50} rx={14} fill={`url(#${gradientId})`} />
            <Eyes cy={44} spread={12} r={7.5} motion={motion} still={still} />
            {/* A mouth as a row of squares, which is the one detail that says "machine". */}
            {[-10, 0, 10].map((dx) => (
              <rect key={dx} x={47 + dx} y={58} width={6} height={6} rx={1.5} fill={css(COLOR.ink)} opacity={0.7} />
            ))}
            <rect x={14} y={38} width={8} height={20} rx={4} fill={deep} />
            <rect x={78} y={38} width={8} height={20} rx={4} fill={deep} />
            <rect x={32} y={74} width={12} height={14} rx={4} fill={deep} />
            <rect x={56} y={74} width={12} height={14} rx={4} fill={deep} />
          </>
        )}

        {kind === "turtle" && (
          <>
            {/* Sequence completion: a turtle, because its shell is a repeating pattern. */}
            <ellipse cx={18} cy={62} rx={11} ry={8} fill={deep} />
            <ellipse cx={82} cy={62} rx={11} ry={8} fill={deep} />
            <circle cx={78} cy={40} r={15} fill={deep} />
            <ellipse cx={50} cy={56} rx={34} ry={26} fill={`url(#${gradientId})`} />
            {/* Three shell segments — the pattern, stated on the creature itself. */}
            {[-16, 0, 16].map((dx) => (
              <path
                key={dx}
                d={`M ${50 + dx} 34 Q ${50 + dx + 7} 56 ${50 + dx} 78`}
                stroke="#fff"
                strokeWidth={3}
                fill="none"
                opacity={0.4}
              />
            ))}
            <Eyes cx={78} cy={38} spread={6} r={4.5} motion={motion} still={still} />
          </>
        )}

        {kind === "bird" && (
          <>
            {/* Spot the difference: a bird, for its sharp eye. */}
            <ellipse cx={30} cy={70} rx={20} ry={12} fill={deep} transform="rotate(-18 30 70)" />
            <ellipse cx={54} cy={54} rx={28} ry={25} fill={`url(#${gradientId})`} />
            <circle cx={44} cy={30} r={17} fill={`url(#${gradientId})`} />
            {/* A tuft, so the head reads as a head and not a second body. */}
            <path d="M 40 15 Q 44 4 52 12" stroke={deep} strokeWidth={4} strokeLinecap="round" fill="none" />
            <Eyes cx={44} cy={28} spread={7} r={5} motion={motion} still={still} />
            <path d="M 27 32 L 12 36 L 27 41 Z" fill={css(COLOR.sunny)} />
            <path d="M 62 76 L 60 90" stroke={deep} strokeWidth={4} strokeLinecap="round" />
            <path d="M 74 74 L 76 88" stroke={deep} strokeWidth={4} strokeLinecap="round" />
          </>
        )}

        {kind === "chameleon" && (
          <>
            {/* The rule changes: a chameleon, the animal that changes with what is around it. */}
            <path
              d="M 84 66 Q 98 62 94 46 Q 91 36 82 40"
              stroke={deep}
              strokeWidth={5}
              strokeLinecap="round"
              fill="none"
            />
            <ellipse cx={52} cy={58} rx={30} ry={22} fill={`url(#${gradientId})`} />
            <circle cx={28} cy={42} r={18} fill={`url(#${gradientId})`} />
            {/* A crest, so the head is a head. */}
            <path d="M 34 26 Q 24 18 14 26 Q 24 30 34 26 Z" fill={deep} />
            <Eyes cx={24} cy={40} spread={0} r={7} motion={motion} still={still} />
            {/* Two feet gripping. */}
            <path d="M 44 78 L 42 90" stroke={deep} strokeWidth={5} strokeLinecap="round" />
            <path d="M 66 78 L 68 90" stroke={deep} strokeWidth={5} strokeLinecap="round" />
          </>
        )}

        {kind === "penguin" && (
          <>
            {/* Hidden pairs: a penguin, because penguins come in pairs and never lose each other. */}
            <ellipse cx={22} cy={62} rx={13} ry={20} fill={deep} transform="rotate(-20 22 62)" />
            <ellipse cx={78} cy={62} rx={13} ry={20} fill={deep} transform="rotate(20 78 62)" />
            <ellipse cx={50} cy={56} rx={30} ry={34} fill={`url(#${gradientId})`} />
            <ellipse cx={50} cy={62} rx={20} ry={26} fill="#fff" opacity={0.95} />
            <Eyes cy={40} spread={10} r={6} motion={motion} still={still} />
            <path d="M 50 50 L 44 57 L 56 57 Z" fill={css(COLOR.sunny)} />
            <ellipse cx={40} cy={92} rx={9} ry={5} fill={css(COLOR.sunny)} />
            <ellipse cx={60} cy={92} rx={9} ry={5} fill={css(COLOR.sunny)} />
          </>
        )}

        {kind === "elephant" && (
          <>
            {/* Smallest to biggest: an elephant, the animal a child names when they mean big. */}
            <ellipse cx={26} cy={48} rx={16} ry={20} fill={deep} />
            <ellipse cx={74} cy={48} rx={16} ry={20} fill={deep} />
            <ellipse cx={50} cy={54} rx={30} ry={30} fill={`url(#${gradientId})`} />
            <Eyes cy={46} spread={12} r={6} motion={motion} still={still} />
            {/* The trunk, curling up — the one line that makes it unmistakable. */}
            <path
              d="M 50 62 Q 48 82 58 88 Q 66 92 66 82"
              stroke={base}
              strokeWidth={11}
              strokeLinecap="round"
              fill="none"
            />
          </>
        )}

        {kind === "bee" && (
          <>
            {/* Counting and arithmetic: a bee, which counts flowers and carries things back in ones. */}
            {/* Wings behind the body, pale so the stripes stay the loudest thing. */}
            <ellipse cx={32} cy={30} rx={17} ry={11} fill="#fff" opacity={0.75} transform="rotate(-28 32 30)" />
            <ellipse cx={70} cy={30} rx={17} ry={11} fill="#fff" opacity={0.75} transform="rotate(28 70 30)" />
            <ellipse cx={50} cy={58} rx={31} ry={26} fill={`url(#${gradientId})`} />
            {/* Three stripes, and three is deliberate: it is a quantity a child can count on the icon. */}
            <path d="M 40 36 Q 50 33 60 36 L 60 44 Q 50 41 40 44 Z" fill={deep} opacity={0.9} />
            <path d="M 34 50 Q 50 46 66 50 L 66 58 Q 50 54 34 58 Z" fill={deep} opacity={0.9} />
            <path d="M 38 66 Q 50 63 62 66 L 60 74 Q 50 71 40 74 Z" fill={deep} opacity={0.9} />
            <Eyes cy={30} spread={9} r={5} motion={motion} still={still} />
            {/* Antennae. */}
            <path d="M 42 18 Q 38 8 32 8" stroke={deep} strokeWidth={3} strokeLinecap="round" fill="none" />
            <path d="M 58 18 Q 62 8 68 8" stroke={deep} strokeWidth={3} strokeLinecap="round" fill="none" />
          </>
        )}

        {kind === "frog" && (
          <>
            {/* The shapes grid: a frog, which sits still and looks at the whole pond at once. */}
            <ellipse cx={22} cy={78} rx={14} ry={9} fill={deep} transform="rotate(-16 22 78)" />
            <ellipse cx={78} cy={78} rx={14} ry={9} fill={deep} transform="rotate(16 78 78)" />
            <ellipse cx={50} cy={60} rx={33} ry={27} fill={`url(#${gradientId})`} />
            {/* Two domes on top for the eyes, which is what makes a frog a frog at icon size. */}
            <circle cx={34} cy={30} r={15} fill={`url(#${gradientId})`} />
            <circle cx={66} cy={30} r={15} fill={`url(#${gradientId})`} />
            <circle cx={34} cy={30} r={9} fill="#fff" />
            <circle cx={66} cy={30} r={9} fill="#fff" />
            <circle cx={34} cy={31} r={4.5} fill={deep} />
            <circle cx={66} cy={31} r={4.5} fill={deep} />
            {/* A wide mouth, drawn as one line: a frog is mostly its smile. */}
            <path d="M 32 64 Q 50 76 68 64" stroke={deep} strokeWidth={4} strokeLinecap="round" fill="none" />
          </>
        )}

        {kind === "ladybird" && (
          <>
            {/*
              * The colour world: a ladybird, which IS a colour pattern — red with black spots, and
              * nothing about it needs naming to be read.
              */}
            <ellipse cx={50} cy={58} rx={33} ry={30} fill={`url(#${gradientId})`} />
            {/* The wing seam down the middle, so the two halves read as a pair. */}
            <path d="M 50 30 L 50 88" stroke={deep} strokeWidth={4} strokeLinecap="round" />
            {/* Four spots, two a side. Symmetric, because the symmetry is the pattern. */}
            <circle cx={36} cy={48} r={6} fill={deep} />
            <circle cx={64} cy={48} r={6} fill={deep} />
            <circle cx={38} cy={70} r={5} fill={deep} />
            <circle cx={62} cy={70} r={5} fill={deep} />
            {/* The head, above the shell. */}
            <circle cx={50} cy={26} r={15} fill={deep} />
            <Eyes cy={24} spread={8} r={4} motion={motion} still={still} />
            <path d="M 42 12 Q 38 3 31 4" stroke={deep} strokeWidth={3} strokeLinecap="round" fill="none" />
            <path d="M 58 12 Q 62 3 69 4" stroke={deep} strokeWidth={3} strokeLinecap="round" fill="none" />
          </>
        )}

        {kind === "parrot" && (
          <>
            {/*
              * The alphabet: a parrot, which is the one bird a Persian-speaking child already
              * associates with saying things back. It is also the only creature here whose beak
              * gives it a profile — useful on a shelf where fifteen others are round.
              */}
            {/* The tail, behind everything, angled so the bird reads as perched rather than flat. */}
            <path d="M 44 74 Q 30 92 20 96 Q 32 86 36 70 Z" fill={deep} />
            <ellipse cx={52} cy={58} rx={29} ry={31} fill={`url(#${gradientId})`} />
            {/* A folded wing: one shape, a shade darker, or the body reads as an egg. */}
            <path d="M 40 40 Q 28 56 34 78 Q 46 70 48 44 Z" fill={deep} opacity={0.55} />
            {/* The head, overlapping the body so there is no seam between them. */}
            <circle cx={54} cy={32} r={20} fill={`url(#${gradientId})`} />
            {/* The face patch, which is what makes it a parrot rather than a generic bird. */}
            <circle cx={62} cy={34} r={11} fill={light} opacity={0.75} />
            {/*
              * The beak: a hook, drawn as one closed path.
              * Two arcs — the upper curving down over the lower — because a triangle would be a
              * finch and this needs to be recognisable at 44 px on a teacher's list.
              */}
            <path
              d="M 70 26 Q 84 30 80 42 Q 76 50 68 46 Q 74 40 70 26 Z"
              fill={css(COLOR.sunny)}
            />
            <Eyes cx={58} cy={30} spread={0} r={6} motion={motion} still={still} />
            {/* Two feet, so it is standing on something rather than floating. */}
            <path d="M 46 88 L 46 94 M 42 94 L 50 94" stroke={deep} strokeWidth={3} strokeLinecap="round" />
            <path d="M 58 88 L 58 94 M 54 94 L 62 94" stroke={deep} strokeWidth={3} strokeLinecap="round" />
          </>
        )}

        {kind === "squirrel" && (
          <>
            {/*
              * Finding a letter in a word: a squirrel with a magnifying glass. It forages through
              * things looking for one small item, which is the game, and its tail makes it the one
              * creature here readable in silhouette at 44 px on a teacher's list.
              */}
            {/* The tail first, behind the body — one thick curl, drawn as a stroke so it keeps its
                weight when the whole thing is scaled down. */}
            <path
              d="M 30 84 Q 6 74 12 48 Q 18 26 40 30"
              stroke={deep}
              strokeWidth={17}
              strokeLinecap="round"
              fill="none"
            />
            <ellipse cx={54} cy={64} rx={24} ry={27} fill={`url(#${gradientId})`} />
            {/* The head, overlapping the body so there is no seam. */}
            <circle cx={57} cy={38} r={19} fill={`url(#${gradientId})`} />
            {/* Ears: two tufts, which is most of what says squirrel rather than mouse. */}
            <path d="M 45 24 Q 43 10 52 16" fill={deep} />
            <path d="M 70 24 Q 73 10 63 16" fill={deep} />
            <Eyes cx={57} cy={37} spread={8} r={5} motion={motion} still={still} />
            {/* The muzzle, so the face has a front. */}
            <circle cx={57} cy={47} r={4} fill={deep} />
            {/*
              * The magnifying glass, held out in front. Drawn last so it sits over everything, and
              * with a pale lens rather than an empty ring — a ring reads as a hoop.
              */}
            <line x1={70} y1={78} x2={84} y2={92} stroke={deep} strokeWidth={6} strokeLinecap="round" />
            <circle cx={64} cy={72} r={15} fill="#fff" opacity={0.55} />
            <circle cx={64} cy={72} r={15} fill="none" stroke={deep} strokeWidth={5} />
          </>
        )}

        {kind === "bat" && (
          <>
            {/*
              * Shadow matching: a bat. The creature a child already associates with the dark, and
              * the only one on this shelf that is itself mostly a silhouette — which is the game.
              */}
            {/* Wings first, behind the body. Two scalloped spans, because a smooth arc reads as a
                cape and the scallop is what says bat. */}
            <path
              d="M 34 46 Q 18 30 6 42 Q 14 44 12 54 Q 22 50 26 60 Q 30 52 38 60 Z"
              fill={deep}
            />
            <path
              d="M 66 46 Q 82 30 94 42 Q 86 44 88 54 Q 78 50 74 60 Q 70 52 62 60 Z"
              fill={deep}
            />
            <ellipse cx={50} cy={56} rx={22} ry={24} fill={`url(#${gradientId})`} />
            {/* Ears: two tall triangles, the other half of what says bat rather than mouse. */}
            <path d="M 36 38 L 32 14 L 48 30 Z" fill={`url(#${gradientId})`} />
            <path d="M 64 38 L 68 14 L 52 30 Z" fill={`url(#${gradientId})`} />
            <Eyes cy={52} spread={9} r={6.5} motion={motion} still={still} />
            {/* Two small fangs under the smile, which is the detail that makes it friendly rather
                than blank — a bat with no mouth reads as an inkblot. */}
            <Smile cy={66} w={10} />
            <path d="M 45 68 L 47 74 L 49 68 Z" fill="#fff" opacity={0.9} />
            <path d="M 51 68 L 53 74 L 55 68 Z" fill="#fff" opacity={0.9} />
          </>
        )}

        {kind === "hare" && (
          <>
            {/*
              * Drive the car: a hare, because a hare is the animal that changes direction
              * mid-run — which is the game in one word.
              */}
            {/* Ears first, long and swept back, since they are the whole silhouette. */}
            <ellipse cx={34} cy={26} rx={7.5} ry={22} fill={deep} transform="rotate(-16 34 26)" />
            <ellipse cx={58} cy={22} rx={7.5} ry={22} fill={deep} transform="rotate(6 58 22)" />
            <ellipse cx={34} cy={28} rx={4} ry={16} fill={light} transform="rotate(-16 34 28)" />
            <ellipse cx={58} cy={24} rx={4} ry={16} fill={light} transform="rotate(6 58 24)" />
            {/* Body leaning forward, so it reads as mid-stride rather than sitting. */}
            <ellipse cx={54} cy={72} rx={28} ry={20} fill={`url(#${gradientId})`} />
            <circle cx={46} cy={50} r={19} fill={`url(#${gradientId})`} />
            {/* A back leg, folded, and the puff of a tail. */}
            <ellipse cx={72} cy={78} rx={13} ry={10} fill={deep} transform="rotate(-14 72 78)" />
            <circle cx={82} cy={62} r={7} fill={light} />
            <Eyes cx={46} cy={48} spread={8} r={5} motion={motion} still={still} />
            <circle cx={34} cy={56} r={3} fill={css(COLOR.watermelon)} />
            <Smile cy={60} w={7} />
          </>
        )}

        {kind === "snail" && (
          <>
            {/*
              * Carrying a motif along a strip: a snail, because a snail carries a repeating spiral
              * and leaves a trail behind it as it goes — the game's shape in one picture.
              */}
            {/* The foot, low and long, so the shell has something to ride on. */}
            <path
              d="M 8 78 Q 8 66 24 66 L 76 66 Q 94 66 92 78 Q 90 86 76 86 L 20 86 Q 8 86 8 78 Z"
              fill={light}
            />
            {/* Eye stalks, before the shell so they read as behind it. */}
            <path d="M 22 66 L 14 40" stroke={light} strokeWidth={5} strokeLinecap="round" />
            <path d="M 32 66 L 32 38" stroke={light} strokeWidth={5} strokeLinecap="round" />
            <circle cx={14} cy={36} r={5.5} fill={css(COLOR.ink)} />
            <circle cx={32} cy={34} r={5.5} fill={css(COLOR.ink)} />
            <circle cx={15.6} cy={34.2} r={1.8} fill="#fff" />
            <circle cx={33.6} cy={32.2} r={1.8} fill="#fff" />
            {/* The shell: a disc with a spiral cut into it, which is the whole silhouette cue. */}
            <circle cx={62} cy={48} r={26} fill={`url(#${gradientId})`} />
            <path
              d="M 62 30 A 18 18 0 1 1 44 48 A 12 12 0 1 0 68 48 A 6 6 0 1 1 62 42"
              stroke={deep}
              strokeWidth={5}
              fill="none"
              strokeLinecap="round"
            />
            <Smile cy={74} w={7} />
          </>
        )}

        {kind === "octopus" && (
          <>
            {/*
              * Colour by the key: an octopus, because an octopus changes colour to order — which is
              * the game said in one picture.
              */}
            {/* Arms first, behind the head. Four curls a side, alternating length so the
                silhouette is not a comb. */}
            {[
              "M 30 62 Q 16 78 22 94",
              "M 40 68 Q 32 88 42 98",
              "M 60 68 Q 68 88 58 98",
              "M 70 62 Q 84 78 78 94",
              "M 22 56 Q 4 68 8 84",
              "M 78 56 Q 96 68 92 84",
            ].map((d, i) => (
              <path
                key={i}
                d={d}
                stroke={deep}
                strokeWidth={9}
                strokeLinecap="round"
                fill="none"
              />
            ))}
            {/* The mantle: tall and domed, which is what separates an octopus from a jellyfish. */}
            <path
              d="M 20 58 Q 20 14 50 14 Q 80 14 80 58 Q 66 68 50 68 Q 34 68 20 58 Z"
              fill={`url(#${gradientId})`}
            />
            {/* Three spots, in the accent lightened — the colour-changing said out loud. */}
            <circle cx={34} cy={30} r={4} fill={light} opacity={0.85} />
            <circle cx={50} cy={24} r={3.2} fill={light} opacity={0.85} />
            <circle cx={65} cy={31} r={4} fill={light} opacity={0.85} />
            <Eyes cy={46} spread={11} r={7} motion={motion} still={still} />
            <Smile cy={60} w={10} />
          </>
        )}

        {kind === "ant" && (
          <>
            {/*
              * Find them all: an ant, because an ant covers ground rather than spotting things —
              * which is exactly what separates this game from spot-the-difference beside it.
              */}
            {/* Legs first, behind the body. Three a side, angled, so the silhouette reads as an
                insect at 44 px on a teacher's list. */}
            {[
              [30, 44, 8, 30],
              [30, 54, 6, 62],
              [34, 62, 12, 84],
              [70, 44, 92, 30],
              [70, 54, 94, 62],
              [66, 62, 88, 84],
            ].map(([x1, y1, x2, y2], i) => (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={deep}
                strokeWidth={4}
                strokeLinecap="round"
              />
            ))}
            {/* Three segments, which is the whole shape of an ant. */}
            <ellipse cx={50} cy={72} rx={20} ry={17} fill={`url(#${gradientId})`} />
            <ellipse cx={50} cy={52} rx={11} ry={10} fill={deep} />
            <circle cx={50} cy={32} r={16} fill={`url(#${gradientId})`} />
            <path d="M 42 20 Q 34 8 26 8" stroke={deep} strokeWidth={3.5} fill="none" strokeLinecap="round" />
            <path d="M 58 20 Q 66 8 74 8" stroke={deep} strokeWidth={3.5} fill="none" strokeLinecap="round" />
            <Eyes cy={30} spread={6.5} r={4.5} motion={motion} still={still} />
            <Smile cy={40} w={7} />
          </>
        )}

        {kind === "beaver" && (
          <>
            {/*
              * The part/whole game: a beaver, which is the animal that makes one thing out of a
              * heap of separate pieces — the game read backwards.
              */}
            {/* The tail, flat and behind everything. It is the whole silhouette cue: without it a
                beaver is a brown blob with teeth. */}
            <ellipse cx={78} cy={76} rx={20} ry={11} fill={deep} transform="rotate(22 78 76)" />
            <ellipse cx={46} cy={60} rx={27} ry={28} fill={`url(#${gradientId})`} />
            <circle cx={44} cy={38} r={21} fill={`url(#${gradientId})`} />
            {/* Small round ears, set wide. */}
            <circle cx={26} cy={24} r={7} fill={deep} />
            <circle cx={62} cy={24} r={7} fill={deep} />
            <Eyes cx={44} cy={35} spread={9} r={5.5} motion={motion} still={still} />
            {/* Muzzle and the two front teeth, which are the other half of "beaver". */}
            <ellipse cx={44} cy={49} rx={11} ry={8} fill={light} />
            <circle cx={44} cy={45} r={3.5} fill={css(COLOR.ink)} />
            <rect x={40} y={51} width={3.6} height={8} rx={1.2} fill="#fff" />
            <rect x={44.5} y={51} width={3.6} height={8} rx={1.2} fill="#fff" />
          </>
        )}

        {kind === "butterfly" && (
          <>
            {/*
              * Mirror building: a butterfly, which is the picture every symmetry lesson for this
              * age band already uses — a child who has folded a painted paper in half has met it.
              *
              * Drawn genuinely symmetric about x = 50, unlike most of the others here, because a
              * lopsided butterfly on the card for the symmetry game would be a small joke at the
              * expense of the thing being taught.
              */}
            <ellipse cx={26} cy={38} rx={19} ry={16} fill={deep} transform="rotate(-18 26 38)" />
            <ellipse cx={74} cy={38} rx={19} ry={16} fill={deep} transform="rotate(18 74 38)" />
            <ellipse cx={31} cy={66} rx={15} ry={13} fill={`url(#${gradientId})`} transform="rotate(14 31 66)" />
            <ellipse cx={69} cy={66} rx={15} ry={13} fill={`url(#${gradientId})`} transform="rotate(-14 69 66)" />
            {/* Two spots, one per upper wing, mirrored. The wings read as decorated rather than
                blank, and they say "these two halves match" without a caption. */}
            <circle cx={26} cy={36} r={5} fill="#fff" opacity={0.55} />
            <circle cx={74} cy={36} r={5} fill="#fff" opacity={0.55} />
            {/* The body, and the antennae that stop it reading as a bow tie. */}
            <ellipse cx={50} cy={52} rx={7} ry={26} fill={base} />
            <path d="M 46 28 Q 40 14 32 12" stroke={base} strokeWidth={3.5} fill="none" strokeLinecap="round" />
            <path d="M 54 28 Q 60 14 68 12" stroke={base} strokeWidth={3.5} fill="none" strokeLinecap="round" />
            <Eyes cy={38} spread={4.5} r={4} motion={motion} still={still} />
          </>
        )}

        {kind === "star" && (
          <>
            {/* Building: a star, because what the child builds becomes a constellation. */}
            <circle
              cx={50}
              cy={50}
              r={42}
              fill={base}
              className={still ? "opacity-24" : `kd-glow ${motion}`}
            />
            <path
              d="M 50 10 L 61 38 L 91 40 L 68 59 L 76 88 L 50 71 L 24 88 L 32 59 L 9 40 L 39 38 Z"
              fill={`url(#${gradientId})`}
            />
            <Eyes cy={48} spread={10} r={6.5} motion={motion} still={still} />
            <Smile cy={62} w={10} />
          </>
        )}
      </g>
    </svg>
  );
}
